import {
  AfterViewInit, Component, ElementRef, NgZone, OnDestroy, ViewChild, effect, inject,
} from '@angular/core';
import { ThemeService } from '../../../core/services/theme.service';

/**
 * AMBIENT BACKDROP
 * ----------------
 * The moving field behind the whole app. Three parallax planes — two soft
 * light pools, a drifting particle field, and a raked grid — each offset by a
 * different fraction of the cursor position and scroll depth, so the page
 * feels like it sits *in* a space rather than on top of a picture.
 *
 * Deliberately generated rather than photographic: nothing to download,
 * nothing to pixelate on a 4K display, and it recolours with the design tokens.
 *
 * Cost control, in order of importance:
 *   · The whole thing stops when the tab is hidden or the canvas scrolls out.
 *   · Cursor and scroll input is sampled into a target and eased in the frame
 *     loop, so listeners never trigger layout or paint themselves.
 *   · `prefers-reduced-motion` and very small viewports get the static
 *     gradient only — no canvas, no listeners, no rAF.
 */
@Component({
  selector: 'app-ambient-backdrop',
  standalone: true,
  templateUrl: './ambient-backdrop.component.html',
  styleUrl: './ambient-backdrop.component.scss',
})
export class AmbientBackdropComponent implements AfterViewInit, OnDestroy {
  @ViewChild('canvas') private canvasRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild('pools') private poolsRef?: ElementRef<HTMLElement>;
  @ViewChild('grid') private gridRef?: ElementRef<HTMLElement>;

  private readonly zone = inject(NgZone);
  private readonly theme = inject(ThemeService);

  /**
   * Particle colours live in the stylesheet like every other colour, so the
   * field recolours with the palette instead of staying pale-blue-on-white.
   * Re-read on the next frame after a theme change rather than inside the
   * effect: by then the new `data-theme` is certainly on the element, and it
   * costs one `getComputedStyle` per switch instead of one per frame.
   */
  private palette = { base: '198, 202, 255', gold: '242, 202, 27', alpha: 1 };
  private paletteDirty = true;

  private ctx: CanvasRenderingContext2D | null = null;
  private frame = 0;
  private particles: Particle[] = [];
  private dpr = 1;
  private w = 0;
  private h = 0;

  /** Where the pointer wants the parallax to be, in the range -1..1. */
  private targetX = 0;
  private targetY = 0;
  /** Where it currently is — eased toward the target every frame. */
  private currentX = 0;
  private currentY = 0;
  private scrollNorm = 0;

  private running = false;
  private cleanups: (() => void)[] = [];

  constructor() {
    effect(() => {
      this.theme.theme();
      this.paletteDirty = true;
    });
  }

  ngAfterViewInit(): void {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return; // static CSS gradient only

    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) return;

    this.ctx = canvas.getContext('2d', { alpha: true });
    if (!this.ctx) return;

    this.resize();
    this.seedParticles();

    // Everything below runs outside Angular: this component never changes
    // application state, so change detection has nothing to do with it.
    this.zone.runOutsideAngular(() => {
      const onPointer = (e: PointerEvent) => {
        this.targetX = (e.clientX / window.innerWidth) * 2 - 1;
        this.targetY = (e.clientY / window.innerHeight) * 2 - 1;
      };
      const onScroll = () => {
        const max = Math.max(1, document.body.scrollHeight - window.innerHeight);
        this.scrollNorm = Math.min(1, window.scrollY / max);
      };
      const onResize = () => { this.resize(); this.seedParticles(); };
      const onVisibility = () => (document.hidden ? this.stop() : this.start());

      window.addEventListener('pointermove', onPointer, { passive: true });
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onResize, { passive: true });
      document.addEventListener('visibilitychange', onVisibility);

      this.cleanups.push(
        () => window.removeEventListener('pointermove', onPointer),
        () => window.removeEventListener('scroll', onScroll),
        () => window.removeEventListener('resize', onResize),
        () => document.removeEventListener('visibilitychange', onVisibility),
      );

      onScroll();
      this.start();
    });
  }

  ngOnDestroy(): void {
    this.stop();
    this.cleanups.forEach(fn => fn());
    this.cleanups = [];
  }

  private start(): void {
    if (this.running) return;
    this.running = true;
    this.frame = requestAnimationFrame(this.tick);
  }

  private stop(): void {
    this.running = false;
    cancelAnimationFrame(this.frame);
  }

  private resize(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) return;
    // Cap DPR at 2 — a particle field gains nothing from a 3x buffer and
    // costs 2.25x the fill rate on phones that can least afford it.
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.w = window.innerWidth;
    this.h = window.innerHeight;
    canvas.width = Math.floor(this.w * this.dpr);
    canvas.height = Math.floor(this.h * this.dpr);
    canvas.style.width = `${this.w}px`;
    canvas.style.height = `${this.h}px`;
    this.ctx?.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  private seedParticles(): void {
    // Scale the count with area so a phone doesn't render a desktop's worth
    // of particles into a fifth of the pixels.
    const area = this.w * this.h;
    const count = Math.round(Math.min(110, Math.max(28, area / 16000)));
    this.particles = Array.from({ length: count }, () => {
      const depth = 0.25 + Math.random() * 0.75; // 0 = far, 1 = near
      return {
        x: Math.random() * this.w,
        y: Math.random() * this.h,
        r: 0.6 + depth * 1.9,
        depth,
        vx: (Math.random() - 0.5) * 0.14 * depth,
        vy: -(0.05 + Math.random() * 0.22) * depth,
        gold: Math.random() > 0.78,
        twinkle: Math.random() * Math.PI * 2,
      };
    });
  }

  private readonly tick = (): void => {
    if (!this.running || !this.ctx) return;

    // Ease toward the pointer target. A low factor is what makes the motion
    // read as "weighted" rather than glued to the cursor.
    this.currentX += (this.targetX - this.currentX) * 0.045;
    this.currentY += (this.targetY - this.currentY) * 0.045;

    this.paintCanvas();
    this.paintCssLayers();

    this.frame = requestAnimationFrame(this.tick);
  };

  private readPalette(): void {
    const cs = getComputedStyle(document.documentElement);
    const read = (name: string, fallback: string) =>
      cs.getPropertyValue(name).trim() || fallback;

    this.palette = {
      base: read('--bd-particle-rgb', '198, 202, 255'),
      gold: read('--bd-particle-gold-rgb', '242, 202, 27'),
      alpha: Number(read('--bd-particle-alpha', '1')) || 1,
    };
  }

  private paintCanvas(): void {
    const ctx = this.ctx!;
    ctx.clearRect(0, 0, this.w, this.h);

    if (this.paletteDirty) {
      this.readPalette();
      this.paletteDirty = false;
    }
    const { base, gold, alpha: tint } = this.palette;

    const px = this.currentX * 26;
    const py = this.currentY * 26;
    const sy = this.scrollNorm * 70;

    for (const p of this.particles) {
      p.x += p.vx;
      p.y += p.vy;
      p.twinkle += 0.012;

      // Wrap rather than respawn, so density never flickers.
      if (p.y < -8) { p.y = this.h + 8; p.x = Math.random() * this.w; }
      if (p.x < -8) p.x = this.w + 8;
      if (p.x > this.w + 8) p.x = -8;

      const x = p.x + px * p.depth;
      const y = p.y + (py + sy) * p.depth;
      const alpha = (0.16 + 0.3 * p.depth) * (0.6 + 0.4 * Math.sin(p.twinkle)) * tint;

      ctx.beginPath();
      ctx.arc(x, y, p.r, 0, Math.PI * 2);
      ctx.fillStyle = p.gold
        ? `rgba(${gold}, ${alpha * 1.25})`
        : `rgba(${base}, ${alpha * 0.75})`;
      ctx.fill();
    }
  }

  private paintCssLayers(): void {
    const pools = this.poolsRef?.nativeElement;
    const grid = this.gridRef?.nativeElement;
    // The three planes move at different rates — that difference *is* the depth.
    if (pools) {
      pools.style.transform =
        `translate3d(${this.currentX * -34}px, ${this.currentY * -34 - this.scrollNorm * 110}px, 0)`;
    }
    if (grid) {
      grid.style.transform =
        `translate3d(${this.currentX * -12}px, ${this.currentY * -12 - this.scrollNorm * 46}px, 0)`;
    }
  }
}

interface Particle {
  x: number;
  y: number;
  r: number;
  depth: number;
  vx: number;
  vy: number;
  gold: boolean;
  twinkle: number;
}
